import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { NgbCalendar, NgbDate, NgbDatepicker, NgbDatepickerMonth, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { FormattedDate } from '../../../../../../models/shared/formatted-date';
import { CommonModule } from '@angular/common';
import { GetMonthNamePipe } from '../../../../pipes/get-month-name.pipe';
import { GetMonthShortNamePipe } from '../../../../pipes/get-month-short-name.pipe';
import { TrackByUtils } from '../../../../../../utils/track-by-utils';

export interface DateSelection {
  startDate: FormattedDate | null;
  endDate: FormattedDate | null;
}

export type NavigationDropdown = 'month' | 'year';

// The calendar removes its month/year options from the DOM on click, so event.target may already be detached.
export function isClickWithinDatepicker(event: Event, host: Element): boolean {
  return event
    .composedPath()
    .some(node => node === host || (node instanceof Element && node.matches('app-datepicker-input-calendar')));
}

const DEFAULT_MINIMUM_YEAR = 1930;
const DEFAULT_MINIMUM_NGB_DATE = NgbDate.from({ year: DEFAULT_MINIMUM_YEAR, month: 1, day: 1 });
const FUTURE_YEARS_WITHOUT_MAX_DATE = 10;

@Component({
  selector: 'app-datepicker-input-calendar',
  templateUrl: './datepicker-input-calendar.component.html',
  standalone: true,
  styleUrls: ['./datepicker-input-calendar.component.scss'],
  imports: [CommonModule, NgbDatepicker, NgbDatepickerMonth, GetMonthNamePipe, GetMonthShortNamePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatepickerInputCalendarComponent implements OnChanges {
  @Input() dateRange = false;
  @Input() isStartDate: boolean = true;
  @Input() initialSelectedDates: DateSelection | null = null;
  @Input() startDate: { year: number; month: number; day?: number } | null = null;
  @Input() minDate: string | null = null;
  @Input() maxDate: string | null = null;
  @Input() markDisabled: ((date: NgbDateStruct) => boolean) | null = null;

  @Output() dateSelected = new EventEmitter<DateSelection>();

  @ViewChild(NgbDatepicker, { read: ChangeDetectorRef }) private datepickerChangeDetector?: ChangeDetectorRef;

  private ngbCalendar = inject(NgbCalendar);
  private changeDetector = inject(ChangeDetectorRef);

  public hoveredDate: NgbDate | null = null;
  public selectedDate: NgbDateStruct | null = null;
  public fromDate: NgbDate | null = null;
  private toDate: NgbDate | null = null;

  public today = this.ngbCalendar.getToday();

  public minNgbDate: NgbDate | null = null;
  public maxNgbDate: NgbDate | null = null;

  public openedDropdown: NavigationDropdown | null = null;
  public monthOptions: number[] = Array.from({ length: 12 }, (_, index) => index + 1);
  public yearOptions: number[] = [];

  public readonly trackByIndex = TrackByUtils.trackByIndex;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialSelectedDates && !!this.initialSelectedDates) {
      this.fromDate = this.initialSelectedDates.startDate?.ngbDate ?? null;
      this.toDate = this.initialSelectedDates.endDate?.ngbDate ?? null;
      this.setStartDate();
    }
    if (changes.startDate) {
      this.setStartDate(this.startDate);
    }
    if (changes.minDate) {
      this.minNgbDate = this.minDate ? FormattedDate.fromUtcString(this.minDate).ngbDate : DEFAULT_MINIMUM_NGB_DATE;
    }
    if (changes.maxDate) {
      this.maxNgbDate = this.maxDate ? FormattedDate.fromUtcString(this.maxDate).ngbDate : null;
    }
    this.updateYearOptions();
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: Event): void {
    if (!this.openedDropdown) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    if (!target.closest('.picker-dropdown')) {
      this.closeDropdown();
    }
  }

  private setStartDate(startDate?: { year: number; month: number; day?: number } | null): void {
    this.startDate = startDate || this.fromDate || this.toDate;
  }

  private dateIsDisabled(date: NgbDate): boolean {
    if (this.minNgbDate && date.before(this.minNgbDate)) {
      return true;
    }
    return !!(this.maxNgbDate && date.after(this.maxNgbDate));
  }

  public handleDateSelection(date: NgbDate): void {
    if (this.dateRange) {
      this.handleDateRangeSelection(date);
    } else {
      this.selectedDate = date;
      this.fromDate = date;
    }
  }

  public handleDateRangeSelection(date: NgbDate): void {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && date && date.after(this.fromDate)) {
      this.toDate = date;
    } else {
      this.toDate = null;
      this.fromDate = date;
    }
  }

  public isHovered(date: NgbDate): boolean {
    if (this.dateRange && !this.dateIsDisabled(date)) {
      return (
        !!this.fromDate &&
        !this.toDate &&
        !!this.hoveredDate &&
        date.after(this.fromDate) &&
        date.before(this.hoveredDate)
      );
    }

    return false;
  }

  public isInside(date: NgbDate): boolean {
    return !!this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  public isRange(date: NgbDate): boolean {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  public navigate(datepicker: NgbDatepicker, num: number): void {
    const { state, calendar } = datepicker;
    datepicker.navigateTo(calendar.getNext(state.focusedDate, 'm', num));
  }

  public toggleDropdown(dropdown: NavigationDropdown, event: Event): void {
    this.openedDropdown = this.openedDropdown === dropdown ? null : dropdown;
    if (this.openedDropdown) {
      const dropdownContainer = (event.currentTarget as HTMLElement).closest('.picker-dropdown');
      setTimeout(() => this.scrollSelectedOptionIntoView(dropdownContainer));
    }
  }

  public closeDropdown(): void {
    this.openedDropdown = null;
    this.changeDetector.markForCheck();
    this.datepickerChangeDetector?.markForCheck();
  }

  public selectMonth(datepicker: NgbDatepicker, month: number): void {
    datepicker.navigateTo({ year: datepicker.state.focusedDate.year, month });
    this.openedDropdown = null;
  }

  public selectYear(datepicker: NgbDatepicker, year: number): void {
    datepicker.navigateTo({ year, month: datepicker.state.focusedDate.month });
    this.openedDropdown = null;
  }

  public isMonthDisabled(datepicker: NgbDatepicker, month: number): boolean {
    const year = datepicker.state.focusedDate.year;
    if (
      this.minNgbDate &&
      (year < this.minNgbDate.year || (year === this.minNgbDate.year && month < this.minNgbDate.month))
    ) {
      return true;
    }
    return !!(
      this.maxNgbDate &&
      (year > this.maxNgbDate.year || (year === this.maxNgbDate.year && month > this.maxNgbDate.month))
    );
  }

  public trackByValue(_: number, value: number): number {
    return value;
  }

  private updateYearOptions(): void {
    const minYear = this.minNgbDate?.year ?? DEFAULT_MINIMUM_YEAR;
    const maxYear = this.maxNgbDate?.year ?? this.today.year + FUTURE_YEARS_WITHOUT_MAX_DATE;
    const yearCount = Math.max(0, maxYear - minYear + 1);
    this.yearOptions = Array.from({ length: yearCount }, (_, index) => minYear + index);
  }

  private scrollSelectedOptionIntoView(dropdownContainer: Element | null): void {
    const panel = dropdownContainer?.querySelector<HTMLElement>('.picker-dropdown-panel');
    const selectedOption = panel?.querySelector<HTMLElement>('.picker-dropdown-option.selected');
    if (panel && selectedOption) {
      panel.scrollTop = selectedOption.offsetTop - panel.clientHeight / 2 + selectedOption.clientHeight / 2;
    }
  }

  public emitDateSelection(): void {
    if (!this.isStartDate && !this.toDate) {
      this.toDate = this.fromDate;
      this.fromDate = null;
    }
    const formattedFromDate = this.fromDate ? FormattedDate.fromNgbDate(this.fromDate) : null;
    const formattedToDate = this.toDate ? FormattedDate.fromNgbDate(this.toDate) : null;
    this.dateSelected.emit({
      startDate: formattedFromDate,
      endDate: formattedToDate
    });
  }

  public clear(): void {
    this.toDate = null;
    this.selectedDate = null;
    this.fromDate = null;
    this.selectedDate = null;
  }
}
